import { LinkType } from './branded-consumer-loan';
import { DEFAULT_LABELS } from './labels';

export type LabelsType = typeof DEFAULT_LABELS;

export type Steps = 0 | 1 | 2 | 3 | 4;

export type Scope = 'carla_se' | 'gronalan_se' | 'apponlinebank_se' | 'apponlinebank_se_';

export const scopeToProductVariant: Record<Scope, string> = {
    carla_se: 'Carla Private loan', // eslint-disable-line camelcase
    gronalan_se: 'Green Loans Private loan', // eslint-disable-line camelcase
    apponlinebank_se: 'VIP Private loan', // eslint-disable-line camelcase
    apponlinebank_se_: 'VIP Private loan', // eslint-disable-line camelcase
};

export type BrandedLoanCalculatorProps = {
    labels: LabelsType;
    fixedRate: number;
    administrativeFee?: number;
    loanAmountMin: number;
    loanAmountMax: number;
    loanAmountDefault: number;
    loanAmountStep?: number;
    tenorYearsMin: number;
    tenorYearsMax: number;
    tenorYearsDefault: number;
    productType: string;
    partner: string;
    loanType?: LoanType;
    chooseLoanType: boolean;
    secciUrl: {
        url: string;
        title: string;
    };
    commonTermsUrl: {
        url: string;
        title: string;
    };
    insuranceLinks?: {
        url: string;
        title: string;
    }[];
    brandColor: string;
    scope?: Scope;
    allowCoapplicant: boolean;
    skipBankIdLogin?: boolean;
};

export type CalculatorStateType = {
    loanAmount: number;
    tenorYears: number;
    showGovIdField: boolean;
};

export type HookProps = {
    fixedRate: number;
    administrativeFee: number;
    loanAmountDefault: number;
    tenorYearsDefault: number;
    productType: string;
};

export type GetLabel = (key: keyof LabelsType, replacements?: Record<string, string | number>) => string;

export type InputProps = {
    label: string;
    suffix: string;
    min: number;
    max: number;
    value: number | null;
    onChange: (value: number | null) => void;
    formatNumber: (number: number) => string;
    defaultStep?: number;
    rangeDescription: string;
};

export type SliderProps = {
    handleSize?: number;
    min: number;
    max: number;
    value: number | null;
    step?: number;
    onChange: (value: number) => void;
    brandColor: string;
    label: string;
    suffix: string;
    formatNumber: (n: number) => string;
};

export type DescriptionProps = {
    title: string;
    text: string;
    commonTermsTitle: string;
    commonTermsUrl: LinkType;
    secciTitle: string;
    secciUrl: LinkType;
    brandColor?: string;
};

export type StepCommonProps = {
    step: number;
    setStep: (step: Steps) => void;
    setValues: (values: FieldValuesType) => void;
    fields: FieldValuesType;
    getValuesFromStorage: () => FieldValuesType | null;
    updateFields: (value: Partial<FieldValuesType>) => void;
    getLabel: GetLabel;
    brandColor: string;
    scrollToTop: () => void;
    allowCoapplicant: boolean;
    chooseLoanType: boolean;
    skipBankIdLogin?: boolean;
    focusInFirstInvalid: () => void;
};

export type FormProps = StepCommonProps & {
    updateState: (key: string, value: number | boolean) => void;
    loanAmountMin: number;
    loanAmountMax: number;
    loanAmountStep?: number;
    tenorYearsMin: number;
    tenorYearsMax: number;
    calculatorState: CalculatorStateType;
    monthlyAmount: number | null;
    formatNumber: (number: number) => string;
    login: (ssn: string) => void;
    scope?: Scope;
};

export type StepTwoProps = StepCommonProps & {
    insuranceLinks: BrandedLoanCalculatorProps['insuranceLinks'];
};

export type LoanType =
    | 'MOTOR'
    | 'WEDDING'
    | 'BOAT'
    | 'INVESTMENT'
    | 'DRIVERS_LICENSE'
    | 'INTERIOR'
    | 'RENOVATION'
    | 'TRAVEL'
    | 'CONSOLIDATE_LOANS'
    | 'DENTAL_OR_OTHER_CARE'
    | 'STUDIES'
    | 'OTHER'
    | 'HOUSING_MORTGAGE_LOAN';
export type EmploymentType = 'FAST_ANSTALLD' | 'VIKARIAT' | 'EGEN_FORETAGARE' | 'PENSIONAR' | 'ARBETSLOS';
export type MaritalStatus = 'SINGLE' | 'MARRIED' | 'DOMESTIC_PARTNER';
export type HabitationForm = 'APARTMENT' | 'CONDOMINIUM' | 'DETACHED_HOUSE';

export type FieldValuesType = {
    governmentId: string;
    phoneMobile: string;
    contactEmail: string;
    purposeOfLoan: LoanType | '';
    applicantMaritalStatus: string;
    numberChildrenNotOfAge: string;
    applicantHabitationForm: string;
    employmentTypeQualifying: '' | 'true' | 'false';
    applicantEmploymentType?: EmploymentType;
    applicantEmployer: string;
    applicantEmploymentYearFrom: string;
    applicantEmploymentMonthFrom: string;
    applicantEmploymentYearUntil: string;
    applicantEmploymentMonthUntil: string;
    montlyIncomeGross: string;
    livingExpenses: string;
    otherLoanExpenses: string;
    insurance: boolean;
    agreementSignType: string;
    payoutAccount: {
        clearingNumber: string;
        accountNumber: string;
        bankName: string;
    };
    approveConditions: boolean;

    coapplicantBool: boolean;
    coapplicantGovernmentId: string;
    coapplicantMobileNumber: string;
    coapplicantEmailAddress: string;
    coapplicantMaritalStatus: string;
    coapplicantHabitationForm: string;
    coapplicantEmploymentTypeQualifying: '' | 'true' | 'false';
    coapplicantEmploymentType?: EmploymentType;
    coapplicantEmployer: string;
    coapplicantEmploymentYearFrom: string;
    coapplicantEmploymentMonthFrom: string;
    coapplicantEmploymentYearUntil: string;
    coapplicantEmploymentMonthUntil: string;
    coapplicantMonthlyIncomeGross: string;
};

export type BaseSubmitPayloadType = {
    applicantGovernmentId: string;
    amount: number;
    time: number;
    applicantMobileNumber: string;
    applicantEmailAddress: string;
    purposeOfLoan: LoanType | '';
    applicantMaritalStatus: string;
    numberChildrenNotOfAge: string;
    applicantHabitationForm: string;
    applicantEmploymentType?: EmploymentType;
    applicantMonthlyIncomeGross: string;
    householdLivingExpenses: string;
    householdOtherLoanExpenses: string;
    payoutAccountNumber: string;
    payoutClearingNumber: string;
    insurance?: 'true';
    approveConditions: string;
    agreementSignType: string;
};

export type SubmitPayloadCoapplicantType = BaseSubmitPayloadType & {
    coapplicantGovernmentId: string;
    coapplicantMobileNumber: string;
    coapplicantEmailAddress: string;
    coapplicantMaritalStatus: string;
    coapplicantHabitationForm: string;
    coapplicantEmploymentType: EmploymentType;
    coapplicantMonthlyIncomeGross: string;
};

export type SubmitPayloadType = BaseSubmitPayloadType | SubmitPayloadCoapplicantType;

export type AccountValues = {
    clearingNumber: string;
    accountNumber: string;
};

export type AccountFieldProps = {
    values: AccountValues;
    setValues: (values: Partial<AccountValues>) => void;
    onValidChange?: (valid: boolean) => void;
    getLabel: GetLabel;
    showValidationErrors?: boolean;
};

export type EmploymentNeed = {
    applicantEmployer?: boolean;
    applicantEmploymentYearFrom?: boolean;
    applicantEmploymentMonthFrom?: boolean;
    applicantEmploymentYearUntil?: boolean;
    applicantEmploymentMonthUntil?: boolean;
    employmentTypeQualifying?: boolean;
    coapplicantEmployer?: boolean;
    coapplicantEmploymentYearFrom?: boolean;
    coapplicantEmploymentYearUntil?: boolean;
    coapplicantEmploymentTypeQualifying?: boolean;
};

export type DecisionResultType = {
    applicationReference: string;
    decision: ApplicationResponseType;
    approvedAmount: number;
    interest: number;
    effectiveInterest: number;
    tenor: number;
    monthlyCost: number;
    totalRepaymentAmount: number;
    totalFeesAndInterest: number;
    adminFee: number;
    arrangementFee: number;
    consolidationDemand: number;
};

export enum ApplicationResponseType {
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    MANUAL_INSPECTION = 'MANUAL_INSPECTION',
}

export type AcceptQuoteResult = {
    status: 'ok' | 'error';
    result?: {
        acceptQuoteResult: {
            accepted: boolean;
            message: string;
        };
    };
    error?: string;
};

export type SubmitApplicationOk = {
    status: 'ok';
    result: {
        submitApplicationExtResult: {
            applicationReference: string;
            decision: ApplicationResponseType;
            approvedAmount: number;
            interest: number;
            tenor: number;
            effectiveInterest: number;
            monthlyCost: number;
            consolidationDemand: number;
            adminFee: number;
            arrangementFee: number;
            documentTypes: string[];
            totalRepaymentAmount: number;
            monthlyAccountFee: number;
            totalFeesAndInterest: number;
        };
    };
    acceptQuoteResult: AcceptQuoteResult | null;
};

export type SubmitApplicationError = {
    status: 'error';
    type: 'ValidationError' | 'ApplicationError'; // extend as needed
    message: string;
};

export type SubmitApplicationResponse = SubmitApplicationOk | SubmitApplicationError;

export const applicationResponseTypeDict = {
    [ApplicationResponseType.APPROVED]: 'Approved',
    [ApplicationResponseType.REJECTED]: 'Rejected',
    [ApplicationResponseType.MANUAL_INSPECTION]: 'Manual Inspection',
};

export type ValidationStateType = Partial<Record<keyof FieldValuesType, boolean>> & {
    allFieldsValid: boolean;
};
